import {
  type RidePassengerInfo,
  usePassengerAuthentications,
} from '@backoffice-frontend/ad-passenger-list'
import { PassengerAuthenticationState } from '@backoffice-frontend/graphql/subscription'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthenticationStatus } from '../PassengerCard/PassengerCard'
import type {
  AdVehicleStreamPassengerCardCustomerInfoFragment,
  AdVehicleStreamPassengerCardStopInfoFragment,
} from '../PassengerCard/PassengerCard.hook'
import { BoardingPassengerCard } from './BoardingPassengerCard'
import { useManualActionPending } from './useManualActionPending'

const PassengerAuthenticationStateToAuthenticationStatus: Record<
  PassengerAuthenticationState,
  AuthenticationStatus
> = {
  [PassengerAuthenticationState.ManualNoShowRequested]:
    AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED,
  [PassengerAuthenticationState.NoShowTriggered]:
    AuthenticationStatus.NO_SHOW_TRIGGERED,
  [PassengerAuthenticationState.InProgress]: AuthenticationStatus.IN_PROGRESS,
  [PassengerAuthenticationState.Confirmed]: AuthenticationStatus.CONFIRMED,
  [PassengerAuthenticationState.Cancelled]: AuthenticationStatus.CANCELLED,
  [PassengerAuthenticationState.Upcoming]: AuthenticationStatus.UPCOMING,
}

type BoardingPassengerWithAuthenticationProps = {
  tripId: string
  vehicleId: string
  customer: AdVehicleStreamPassengerCardCustomerInfoFragment | null
  ridePassengerInfo?: RidePassengerInfo | null
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
  stopServiceAreaId?: string | null
  eta: Date
  isBoarded: boolean
  isNoShow: boolean
}
export const BoardingPassengerCardWithAuthentication = ({
  tripId,
  vehicleId,
  customer,
  ridePassengerInfo,
  waypointStop,
  stopServiceAreaId,
  eta,
  isBoarded,
  isNoShow,
}: BoardingPassengerWithAuthenticationProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { getPassengerAuthentication } = usePassengerAuthentications()
  const authenticationState = getPassengerAuthentication(tripId)

  const notifyTimeout = useCallback(() => {
    enqueueMoiaErrorNotification(
      t('adFleetMap_toast_passengerCheckInFailed_label'),
    )
  }, [enqueueMoiaErrorNotification, t])

  const { isPending, startPending } = useManualActionPending({
    authenticationState,
    onTimeout: notifyTimeout,
  })

  const authenticationStatus = authenticationState
    ? PassengerAuthenticationStateToAuthenticationStatus[authenticationState]
    : AuthenticationStatus.UPCOMING

  return (
    <BoardingPassengerCard
      tripId={tripId}
      vehicleId={vehicleId}
      customer={customer}
      ridePassengerInfo={ridePassengerInfo}
      waypointStop={waypointStop}
      stopServiceAreaId={stopServiceAreaId}
      eta={eta}
      authenticationStatus={authenticationStatus}
      isPending={isPending}
      onActionConfirmed={startPending}
      isNoShow={isNoShow}
      isBoarded={isBoarded}
    />
  )
}
